import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { reviewService, type DiscussionPoint } from '@/services/api/reviewService'
import { sessionManager, SessionExpiredError } from '@/services/sessionManager'
import toast from 'react-hot-toast'
import { KPAData } from '@/types/evidence'
import { useAuth } from '@/providers/AuthProvider'
import type { RecordedByHr } from '@/components/performance/review/RecordedByHrNote'

/**
 * A save can fail because the auth session expired (token lapsed past silent
 * refresh). When it does, surfacing only a small "failed to save" toast lets
 * the user keep typing into a void and lose work. Detect it so the caller can
 * raise the global Session Expired modal instead. Covers both the typed
 * SessionExpiredError (fetch path) and the name/message-based variant.
 */
function isSessionExpiredError(error: unknown): boolean {
  return (
    error instanceof SessionExpiredError ||
    (error as any)?.name === 'SessionExpiredError' ||
    (error as any)?.message === 'Session expired'
  )
}

// Completing stops with this rather than lock out discussion points that weren't saved.
const UNSAVED_POINTS = "Your latest discussion points weren't saved, so the check-in wasn't completed. Please try again."

interface UseReviewSessionParams {
  memberId: string
  managerId?: string
  memberName: string
  reviewType: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin' | 'adhoc'
  reviewPeriod: 'MIDYEAR' | 'YEAREND'
  userRole: 'manager' | 'employee'
  initialReviewSessionId?: string
  readOnly?: boolean
  kpaData: KPAData[]
  managerRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>
  managerOverallRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  managerComments: Record<string, string>
  onClose: () => void
  onReviewCompleted?: () => void
}

export function useReviewSession({
  memberId,
  managerId,
  memberName,
  reviewType,
  reviewPeriod,
  userRole,
  initialReviewSessionId,
  readOnly = false,
  kpaData,
  managerRatings,
  managerOverallRating,
  managerComments,
  onClose,
  onReviewCompleted,
}: UseReviewSessionParams) {
  const { user } = useAuth()
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(initialReviewSessionId || null)
  const [isReviewCompleted, setIsReviewCompleted] = useState(false)
  // Too Soon To Rate (HR UAT list #20): first hire less than three months before
  // the check-in end date, so the only rating that can be chosen is TSTR.
  const [tooSoonToRate, setTooSoonToRate] = useState<{ first_hire: string; tstr_line: string; checkin_end: string } | null>(null)
  // HR recorded this check-in on the People Leader's behalf (#24), or its calibration
  // was held outside the platform and recorded by HR (#26).
  const [recordedByHr, setRecordedByHr] = useState<RecordedByHr | null>(null)
  const [calibrationRecordedByHr, setCalibrationRecordedByHr] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false)

  const [kpaRatings, setKpaRatings] = useState<Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>>(managerRatings)
  const [overallRating, setOverallRating] = useState<'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | null>(managerOverallRating || null)
  const [ratingJustification, setRatingJustification] = useState<string>('')

  const [kpaDiscussionPoints, setKpaDiscussionPoints] = useState<Record<string, {
    wentWell: DiscussionPoint[]
    challenges: DiscussionPoint[]
    actions: DiscussionPoint[]
  }>>({})

  const isFormalReview = reviewType === 'year-end' || reviewType === 'mid-year'
  const canRate = userRole === 'manager' && !isReviewCompleted && !readOnly
  const displayRatings = userRole === 'employee' ? managerRatings : kpaRatings

  const transformKpaDataForPeriod = useCallback((data: KPAData[]): KPAData[] => {
    if (import.meta.env.DEV) console.log(`🔄 transformKpaDataForPeriod called for ${reviewPeriod}`)
    return data.map(kpa => {
      const transformedObjectives = kpa.individualObjectives?.map(obj => ({
        ...obj,
        kpis: obj.kpis?.map(kpi => {
          const midyearVal = (kpi as any).midyear_value
          const yearendVal = (kpi as any).yearend_value
          const periodValue = reviewPeriod === 'MIDYEAR'
            ? (midyearVal ?? 0)
            : (yearendVal ?? 0)
          const periodProgress = reviewPeriod === 'MIDYEAR'
            ? ((kpi as any).midyear_progress ?? 0)
            : ((kpi as any).yearend_progress ?? 0)
          if (import.meta.env.DEV) console.log(`   KPI "${kpi.name}" in obj: midyear=${midyearVal}, yearend=${yearendVal}, using=${periodValue} for ${reviewPeriod}`)
          return {
            ...kpi,
            current_value: periodValue,
            progress: periodProgress
          }
        })
      }))
      const transformedUnderlyingKPIs = kpa.underlyingKPIs?.map(kpi => {
        const midyearVal = (kpi as any).midyear_value
        const yearendVal = (kpi as any).yearend_value
        const periodValue = reviewPeriod === 'MIDYEAR'
          ? (midyearVal ?? 0)
          : (yearendVal ?? 0)
        const periodProgress = reviewPeriod === 'MIDYEAR'
          ? ((kpi as any).midyear_progress ?? 0)
          : ((kpi as any).yearend_progress ?? 0)
        if (import.meta.env.DEV) console.log(`   KPI "${kpi.name}": midyear=${midyearVal}, yearend=${yearendVal}, using=${periodValue} for ${reviewPeriod}`)
        return {
          ...kpi,
          current_value: periodValue,
          progress: periodProgress
        }
      })

      const calcObjProgress = (obj: any) => {
        if (!obj.kpis || obj.kpis.length === 0) return obj.progress || 0
        const total = obj.kpis.reduce((sum: number, kpi: any) => {
          const p = kpi.target_value ? (kpi.current_value / kpi.target_value) * 100 : 0
          return sum + Math.min(p, 100)
        }, 0)
        return Math.round(total / obj.kpis.length)
      }
      const objectives = transformedObjectives || []
      const recalculatedAvgProgress = objectives.length > 0
        ? Math.round(objectives.reduce((sum, obj) => sum + calcObjProgress(obj), 0) / objectives.length)
        : (kpa.averageProgress || 0)

      return {
        ...kpa,
        averageProgress: recalculatedAvgProgress,
        individualObjectives: transformedObjectives,
        underlyingKPIs: transformedUnderlyingKPIs
      }
    })
  }, [reviewPeriod])

  const [kpaDataWithSnapshots, setKpaDataWithSnapshots] = useState<KPAData[]>(() => transformKpaDataForPeriod(kpaData))

  useEffect(() => {
    if (import.meta.env.DEV) console.log(`📊 Transforming KPA data for ${reviewPeriod} period - checking KPIs for period-specific values`)
    const transformed = transformKpaDataForPeriod(kpaData)

    const sampleKPI = transformed[0]?.underlyingKPIs?.[0]
    if (sampleKPI) {
      if (import.meta.env.DEV) console.log(`   Sample KPI "${sampleKPI.name}": current_value=${sampleKPI.current_value} (from ${reviewPeriod === 'MIDYEAR' ? 'midyear_value' : 'yearend_value'})`)
    }

    setKpaDataWithSnapshots(transformed)
  }, [kpaData, reviewPeriod, transformKpaDataForPeriod])

  useEffect(() => {
    if (initialReviewSessionId && initialReviewSessionId !== reviewSessionId) {
      if (import.meta.env.DEV) console.log('🔄 Syncing reviewSessionId from prop:', initialReviewSessionId)
      setReviewSessionId(initialReviewSessionId)
    }
  }, [initialReviewSessionId, reviewSessionId, kpaData])

  useEffect(() => {
    const loadExistingDiscussionPoints = async () => {
      if (!reviewSessionId) return

      try {
        if (import.meta.env.DEV) console.log('📖 Loading existing review session:', reviewSessionId)
        const session = await reviewService.getReviewSession(reviewSessionId)
        if (import.meta.env.DEV) console.log('📖 Session response:', session)
        console.log('📖 Session status:', session.status, 'Type:', typeof session.status)
        if (import.meta.env.DEV) console.log('📖 KPA ratings:', session.kpa_ratings)

        setTooSoonToRate(session.too_soon_to_rate || null)
        setRecordedByHr(session.recorded_by_hr || null)
        setCalibrationRecordedByHr(!!session.calibration_recorded_by_hr)

        const isCompleted = session.status === 'COMPLETED' ||
                             session.status === 'completed' ||
                             session.status?.toString().toUpperCase() === 'COMPLETED'

        if (import.meta.env.DEV) console.log('🔍 Is review completed?', isCompleted)

        if (isCompleted) {
          setIsReviewCompleted(true)
          if (import.meta.env.DEV) console.log('🔒 Review is completed - read-only mode ACTIVATED')

          // Preferred path: a kpa_data_snapshot was captured at completion.
          // Replay it verbatim so the user sees the exact objectives /
          // KPIs / targets that were on the page during the conversation.
          // Falls back to the legacy KPI-only snapshot merge if the session
          // pre-dates this feature.
          const sessionAny = session as any
          if (Array.isArray(sessionAny.kpa_data_snapshot) && sessionAny.kpa_data_snapshot.length > 0) {
            if (import.meta.env.DEV) console.log('📸 Using kpa_data_snapshot from completion — bypassing live kpaData merge')
            setKpaDataWithSnapshots(sessionAny.kpa_data_snapshot as KPAData[])
          } else if (session.kpa_ratings && session.kpa_ratings.length > 0) {
            // Legacy completed sessions don't have a full kpa_data_snapshot —
            // only per-KPA `kpi_snapshot.kpis[]` arrays of (kpi_id, value).
            //
            // Old behaviour leaked: for KPIs not in the snapshot, we let the
            // LIVE value fall through. That's wrong — a KPI added to the role
            // *after* the check-in completed would render as if it had been
            // part of the conversation. We now hide any KPI whose id isn't in
            // the snapshot, prune objectives left with zero KPIs, and prune
            // KPAs left with zero objectives. The result is a faithful (if
            // smaller) view of what was actually on the page at the time.
            //
            // We union snapshot ids ACROSS all kpa_ratings rows for the
            // matching KPA, because some legacy sessions stored ratings under
            // both numeric and slug keys (issue D from the analysis) and the
            // KPI ids can be split across them.
            const mergedKpaData = kpaData.map(kpa => {
              const matchingRatings = session.kpa_ratings.filter((r: any) =>
                r.kpa_name === kpa.name ||
                r.kpa_id === kpa.id ||
                r.kpa_name === kpa.id ||
                r.kpa_id === kpa.name
              )

              const snapshotMap = new Map<string, number>()
              for (const rating of matchingRatings) {
                const snap = (rating as any).kpi_snapshot
                if (snap && Array.isArray(snap.kpis)) {
                  for (const k of snap.kpis) {
                    if (k && k.kpi_id != null) {
                      snapshotMap.set(String(k.kpi_id), k.current_value)
                    }
                  }
                }
              }

              if (snapshotMap.size === 0) {
                // No KPI VALUES were captured for this KPA at completion. The old
                // behaviour dropped the entire KPA — but an empty per-KPA snapshot
                // gives us NO basis to decide what was "on the page", and the manager
                // DID rate this KPA, so there was real content. Dropping everything is
                // exactly how a complete review (objectives + KPIs all intact) rendered
                // as "No objectives configured" when its kpa_data_snapshot ALSO failed
                // to capture. Fall back to the LIVE objectives + KPIs for this KPA —
                // the real configured data, and the behaviour the backend already
                // promises ("reopening will fall back to live data") when no snapshot
                // was captured.
                if (import.meta.env.DEV) console.log(`📸 No KPI snapshot for KPA "${kpa.name}" — falling back to live objectives/KPIs`)
                return kpa
              }

              if (import.meta.env.DEV) console.log(`📸 KPA "${kpa.name}": ${snapshotMap.size} KPIs in snapshot — pruning others`)

              // Keep only KPIs whose ids are in the snapshot, and replace
              // their current_value with the frozen snapshot value.
              const prunedObjectives = (kpa.individualObjectives || [])
                .map(obj => {
                  const keptKpis = (obj.kpis || []).filter(k => snapshotMap.has(k.id)).map(k => ({
                    ...k,
                    current_value: snapshotMap.get(k.id) as number,
                  }))
                  return { ...obj, kpis: keptKpis }
                })
                // Drop objectives that now have zero KPIs — they weren't
                // actually discussed. Keep ones that were never KPI-bearing
                // (length === 0 on both live and pruned) only if the live
                // shape had no KPIs to begin with; otherwise prune.
                .filter(obj => (obj.kpis || []).length > 0)

              const prunedUnderlyingKPIs = (kpa.underlyingKPIs || [])
                .filter(k => snapshotMap.has(k.id))
                .map(k => ({ ...k, current_value: snapshotMap.get(k.id) as number }))

              return {
                ...kpa,
                individualObjectives: prunedObjectives,
                underlyingKPIs: prunedUnderlyingKPIs,
              }
            })
            // Drop KPAs that have no surviving content. The user wanted
            // "hide them if they were not part of the completed review",
            // and that includes whole KPAs that contributed nothing.
            .filter(kpa =>
              (kpa.individualObjectives || []).length > 0 ||
              (kpa.underlyingKPIs || []).length > 0
            )

            setKpaDataWithSnapshots(mergedKpaData)
            if (import.meta.env.DEV) console.log(`📸 Pruned KPA data to historical snapshot: ${mergedKpaData.length} KPAs surviving`)
          }
        } else {
          setIsReviewCompleted(false)
          setKpaDataWithSnapshots(transformKpaDataForPeriod(kpaData))
          if (import.meta.env.DEV) console.log(`✏️ Review is in progress - edit mode (${reviewPeriod} period)`)
        }

        if (session.kpa_ratings && session.kpa_ratings.length > 0) {
          const loadedPoints: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }> = {}
          const loadedRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'> = {}

          session.kpa_ratings.forEach(kpaRating => {
            const matchedKpa = kpaData.find((k: any) =>
              k.id === kpaRating.kpa_id ||
              k.id === kpaRating.kpa_name ||
              k.name === kpaRating.kpa_name
            )
            const kpaKey = matchedKpa?.id || kpaRating.kpa_id || kpaRating.kpa_name

            if (kpaRating.rating) {
              loadedRatings[kpaKey] = kpaRating.rating as 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
            }

            if (kpaRating.discussion_responses) {
              const responses = kpaRating.discussion_responses

              loadedPoints[kpaKey] = {
                wentWell: Array.isArray(responses.wentWell)
                  ? responses.wentWell.map((p: any) => ({
                      ...p,
                      timestamp: new Date(p.timestamp)
                    }))
                  : [],
                challenges: Array.isArray(responses.challenges)
                  ? responses.challenges.map((p: any) => ({
                      ...p,
                      timestamp: new Date(p.timestamp)
                    }))
                  : [],
                actions: Array.isArray(responses.actions)
                  ? responses.actions.map((p: any) => ({
                      ...p,
                      timestamp: new Date(p.timestamp)
                    }))
                  : []
              }
            }
          })

          if (import.meta.env.DEV) console.log('📖 Loaded discussion points:', loadedPoints)
          console.log('📖 Loaded ratings:', loadedRatings)
          setKpaDiscussionPoints(loadedPoints)
          setKpaRatings(loadedRatings)
        }

        // Prefer the CALIBRATED rating (set when a calibration session is
        // published) over the manager's initial rating, so the team member sees
        // their final post-calibration rating — not the pre-calibration one.
        const finalRating = (session as any).calibrated_rating || session.initial_rating
        if (finalRating) {
          setOverallRating(finalRating as 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr')
          if (import.meta.env.DEV) console.log('📖 Loaded overall rating:', finalRating)
        }
        if (session.meeting_notes) {
          setRatingJustification(session.meeting_notes)
          if (import.meta.env.DEV) console.log('📖 Loaded rating justification')
        }
      } catch (error) {
        console.error('❌ Error loading discussion points:', error)
      }
    }

    loadExistingDiscussionPoints()
  }, [reviewSessionId, kpaData, transformKpaDataForPeriod, reviewPeriod])

  const activeKpaData = useMemo(() =>
    [...kpaDataWithSnapshots].sort((a: any, b: any) => (a.sort_order || 999) - (b.sort_order || 999)),
    [kpaDataWithSnapshots]
  )

  const ensureReviewSession = useCallback(async () => {
    if (reviewSessionId) return reviewSessionId

    if (!managerId) {
      console.error('❌ Cannot create session: managerId is required')
      return null
    }

    try {
      if (import.meta.env.DEV) console.log('📝 Creating new review session:', { memberId, managerId, reviewType, userRole })
      const newSession = await reviewService.createReviewSession({
        employee_id: memberId,
        manager_id: managerId,
        review_type: reviewType as any,
      })

      setReviewSessionId(newSession.id)
      if (import.meta.env.DEV) console.log('✅ Review session created:', newSession.id)
      return newSession.id
    } catch (error) {
      console.error('❌ Error creating review session:', error)
      toast.error('Failed to create check-in')
      return null
    }
  }, [reviewSessionId, managerId, memberId, reviewType, userRole])

  // Returns true only if the rating actually PERSISTED. Callers roll back their
  // optimistic state on false — a silently-dropped save (e.g. expired token on
  // the first KPA) must NOT leave a KPA looking rated when it has no row, or the
  // completion gate passes and that KPA disappears from calibration downstream.
  const saveKPARating = useCallback(async (
    kpaId: string,
    rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr',
    comments?: string,
    responses?: Record<string, string>
  ): Promise<boolean> => {
    if (!canRate) return false

    const sessionId = await ensureReviewSession()
    if (!sessionId) return false

    setIsSaving(true)
    try {
      const kpa = activeKpaData.find(k => k.id === kpaId)
      await reviewService.saveKPARating(sessionId, {
        kpa_id: kpaId,
        kpa_name: kpa?.name || kpaId,
        rating,
        manager_comments: comments,
        discussion_responses: responses,
      })
      return true
    } catch (error) {
      if (isSessionExpiredError(error)) {
        // Raise the global Session Expired modal instead of a silent toast —
        // the user needs to re-auth, not lose their edit behind a dialog.
        sessionManager.markExpired()
      } else {
        console.error('Error saving KPA rating:', error)
        toast.error('Failed to save indicator')
      }
      return false
    } finally {
      setIsSaving(false)
    }
  }, [canRate, ensureReviewSession, kpaData])

  const handleKPARating = async (kpaId: string, rating: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr') => {
    const prevRating = kpaRatings[kpaId]
    setKpaRatings(prev => ({
      ...prev,
      [kpaId]: rating
    }))

    const ok = await saveKPARating(
      kpaId,
      rating,
      managerComments[kpaId],
      kpaDiscussionPoints[kpaId] as any
    )

    // Roll back the optimistic rating if the save didn't persist, so the
    // completion gate (every KPA rated) can't pass with an unsaved KPA.
    if (!ok) {
      setKpaRatings(prev => {
        const next = { ...prev }
        if (prevRating === undefined) {
          delete next[kpaId]
        } else {
          next[kpaId] = prevRating
        }
        return next
      })
    }
  }

  // Resolves to whether the points were saved.
  const saveDiscussionPoints = useCallback(async (
    kpaId: string,
    points?: { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }
  ): Promise<boolean> => {
    if (import.meta.env.DEV) console.log('💾 Saving discussion points for KPA:', kpaId)

    const sessionId = await ensureReviewSession()
    if (!sessionId) {
      if (import.meta.env.DEV) console.log('❌ No session ID, cannot save')
      setIsSaving(false)
      return false
    }

    try {
      const kpa = activeKpaData.find(k => k.id === kpaId)
      const discussionPoints = points || kpaDiscussionPoints[kpaId]

      if (import.meta.env.DEV) console.log('💾 Saving data:', { sessionId, kpaId, points: discussionPoints })

      // A discussion-point save must NOT write the rating column. This POSTs to
      // the shared kpa-ratings endpoint; sending rating: kpaRatings[kpaId] (which
      // is undefined for a not-yet-rated KPA, and can be stale in a debounced /
      // unmount flush) previously clobbered a real rating back to NULL — the KPA
      // then vanished from calibration. Omit rating entirely; the backend leaves
      // the persisted rating untouched when the payload carries none.
      await reviewService.saveKPARating(sessionId, {
        kpa_id: kpaId,
        kpa_name: kpa?.name || kpaId,
        manager_comments: managerComments[kpaId],
        discussion_responses: discussionPoints as any,
      })
      if (import.meta.env.DEV) console.log('✅ Discussion points saved successfully')
      return true
    } catch (error) {
      if (isSessionExpiredError(error)) {
        // Raise the global Session Expired modal instead of a silent toast —
        // otherwise the just-typed discussion point is lost with no prompt.
        sessionManager.markExpired()
      } else {
        console.error('❌ Error saving discussion points:', error)
        toast.error('Failed to save discussion points')
      }
      return false
    } finally {
      setIsSaving(false)
    }
  }, [ensureReviewSession, activeKpaData, kpaDiscussionPoints, kpaRatings, managerComments])

  // Per-KPA debounce timers, kept in a ref so they survive re-renders AND
  // useCallback recreation. Previously a SINGLE shared timer was used for all
  // KPAs: editing KPA B within 2s of editing KPA A called clearTimeout on
  // A's pending save, so A's edit was silently dropped. Keying the timer by
  // kpaId means each KPA's save fires independently.
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  // Latest pending points per KPA, so an unmount / page-unload flush can still
  // fire a save that's inside its debounce window — otherwise the timer dies
  // with the JS context and the just-added point is silently dropped.
  const pendingPointsRef = useRef<Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }>>({})
  // Saves already on their way to the server, so completing can wait for them.
  const inFlightSavesRef = useRef<Set<Promise<boolean>>>(new Set())
  const trackSave = useCallback((save: Promise<boolean>) => {
    inFlightSavesRef.current.add(save)
    void save.finally(() => inFlightSavesRef.current.delete(save))
    return save
  }, [])
  const debouncedSavePoints = useCallback(
    (kpaId: string, points?: { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }) => {
      const timers = saveTimersRef.current
      if (timers[kpaId]) clearTimeout(timers[kpaId])
      if (points) pendingPointsRef.current[kpaId] = points
      timers[kpaId] = setTimeout(() => {
        delete timers[kpaId]
        delete pendingPointsRef.current[kpaId]
        trackSave(saveDiscussionPoints(kpaId, points))
      }, 2000)
    },
    [saveDiscussionPoints, trackSave]
  )

  // Send every save still inside its debounce window now, wait for those and any
  // already on their way, and say whether all of them were saved. Completing a
  // check-in waits for this: once it is completed, the server leaves its points as
  // stored (HR UAT list #4), so a save landing after it would be dropped.
  const flushPendingSaves = useCallback(async (): Promise<boolean> => {
    const timers = saveTimersRef.current
    Object.keys(timers).forEach((kpaId) => {
      clearTimeout(timers[kpaId])
      delete timers[kpaId]
      const pts = pendingPointsRef.current[kpaId]
      if (pts) {
        delete pendingPointsRef.current[kpaId]
        trackSave(saveDiscussionPoints(kpaId, pts))
      }
    })
    const results = await Promise.all(Array.from(inFlightSavesRef.current))
    return results.every(Boolean)
  }, [saveDiscussionPoints, trackSave])

  // Flush pending debounced saves when the workspace unmounts (in-app navigation
  // / switching member) OR the page is refreshed/closed. Without this, adding a
  // discussion point and refreshing within the 2s window drops the point — the
  // exact "my comment isn't reflecting after refresh" bug.
  useEffect(() => {
    const flushPending = () => { void flushPendingSaves() }
    window.addEventListener('beforeunload', flushPending)
    return () => {
      window.removeEventListener('beforeunload', flushPending)
      flushPending()
    }
  }, [flushPendingSaves])

  const handleAddPoint = useCallback((kpaId: string, step: 'wentWell' | 'challenges' | 'actions', content: string) => {
    if (import.meta.env.DEV) console.log('🔵 Adding discussion point:', { kpaId, step, content })
    const newPoint: DiscussionPoint = {
      id: crypto.randomUUID(),
      content,
      addedBy: userRole,
      // Attribute to whoever is logged in (People Leader when conducting, the
      // member when self-preparing) — NOT the review subject. memberName is only
      // a fallback for the brief window before the auth user is loaded.
      addedByName: user?.displayName || memberName,
      timestamp: new Date(),
    }

    setKpaDiscussionPoints(prev => {
      const updated = {
        ...prev,
        [kpaId]: {
          ...prev[kpaId],
          wentWell: prev[kpaId]?.wentWell || [],
          challenges: prev[kpaId]?.challenges || [],
          actions: prev[kpaId]?.actions || [],
          [step]: [...(prev[kpaId]?.[step] || []), newPoint]
        }
      }
      if (import.meta.env.DEV) console.log('🔵 Updated discussion points:', updated)

      setTimeout(() => {
        if (import.meta.env.DEV) console.log('🔵 Triggering debounced save for KPA:', kpaId)
        setIsSaving(true)
        debouncedSavePoints(kpaId, updated[kpaId])
      }, 0)

      return updated
    })
  }, [userRole, memberName, user?.displayName, debouncedSavePoints])

  const handleDeletePoint = useCallback((kpaId: string, step: 'wentWell' | 'challenges' | 'actions', pointId: string) => {
    setKpaDiscussionPoints(prev => {
      const updated = {
        ...prev,
        [kpaId]: {
          ...prev[kpaId],
          wentWell: prev[kpaId]?.wentWell || [],
          challenges: prev[kpaId]?.challenges || [],
          actions: prev[kpaId]?.actions || [],
          // Soft delete: tombstone instead of removing. The tombstone is
          // transmitted on save so the backend's delete-wins merge keeps the
          // deletion converged — a concurrent stale writer (employee/manager)
          // can no longer resurrect the point. Tombstones are filtered out of
          // every consumer at the hook boundary (visibleKpaDiscussionPoints).
          [step]: prev[kpaId]?.[step]?.map(p => p.id === pointId ? { ...p, deleted: true } : p) || []
        }
      }

      setTimeout(() => {
        setIsSaving(true)
        debouncedSavePoints(kpaId, updated[kpaId])
      }, 0)

      return updated
    })
  }, [debouncedSavePoints])

  // Edit a point's wording (HR UAT list #4). Allowed until the check-in is
  // completed, for the side that added it; the server merges by id, so the new
  // wording replaces the old one and the other side's points are untouched.
  const handleUpdatePoint = useCallback((kpaId: string, step: 'wentWell' | 'challenges' | 'actions', pointId: string, content: string) => {
    setKpaDiscussionPoints(prev => {
      const updated = {
        ...prev,
        [kpaId]: {
          ...prev[kpaId],
          wentWell: prev[kpaId]?.wentWell || [],
          challenges: prev[kpaId]?.challenges || [],
          actions: prev[kpaId]?.actions || [],
          [step]: prev[kpaId]?.[step]?.map(p => p.id === pointId ? { ...p, content } : p) || []
        }
      }

      setTimeout(() => {
        setIsSaving(true)
        debouncedSavePoints(kpaId, updated[kpaId])
      }, 0)

      return updated
    })
  }, [debouncedSavePoints])

  const handleCompleteReview = useCallback(async () => {
    if (!reviewSessionId) {
      toast.error('No check-in found')
      return
    }

    // Belt-and-braces: a formal check-in must have KPAs to rate. An empty tree
    // means the objectives/KPIs failed to load — completing would persist an
    // empty snapshot and a blind rating, so block it instead of silently passing
    // the `every()` check below (which is vacuously true on an empty array).
    if (activeKpaData.length === 0) {
      toast.error('No objectives loaded for this check-in. Please reopen it before completing.')
      return
    }

    const allRated = activeKpaData.every(kpa => kpaRatings[kpa.id])
    if (!allRated) {
      toast.error('Please rate all KPAs before completing the check-in')
      return
    }

    if (!overallRating) {
      toast.error('Please select an overall Impact & Growth Indicator')
      return
    }

    if (!ratingJustification.trim()) {
      toast.error('Please provide a justification for the indicator')
      return
    }

    try {
      // Discussion points still on their way go first: once completed, the
      // check-in keeps its points as stored, and a later save is dropped.
      if (!(await flushPendingSaves())) {
        toast.error(UNSAVED_POINTS)
        return
      }

      // Capture the live KPA tree at completion so reopening this session
      // later replays exactly what was on the page during the conversation,
      // independent of subsequent edits to objectives or KPIs.
      await reviewService.updateReviewSession(reviewSessionId, {
        overall_rating: overallRating,
        overall_comments: ratingJustification,
        is_complete: 1,
        kpa_data_snapshot: activeKpaData,
      })

      toast.success('Check-in completed and submitted successfully! 🎉')

      if (onReviewCompleted) {
        onReviewCompleted()
      }

      setTimeout(() => {
        onClose()
      }, 1500)
    } catch (error: any) {
      console.error('Failed to complete review:', error)
      // Surface the server's reason when it rejects the completion (e.g. the
      // hard gate naming a KPA with no persisted rating) — the manager needs
      // to know WHICH KPA to re-rate, not just that it failed.
      toast.error(error?.response?.data?.detail || 'Failed to complete check-in. Please try again.')
    }
  }, [reviewSessionId, activeKpaData, kpaRatings, overallRating, ratingJustification, flushPendingSaves, onClose, onReviewCompleted])

  const handleCompleteInformalReview = useCallback(async (currentKPA: KPAData | undefined) => {
    const sessionId = await ensureReviewSession()
    if (!sessionId) {
      toast.success('Check-in completed!')
      onClose()
      return
    }

    try {
      // Every KPA's points still on their way go first (see handleCompleteReview).
      if (!(await flushPendingSaves())) {
        toast.error(UNSAVED_POINTS)
        return
      }
      if (currentKPA && kpaDiscussionPoints[currentKPA.id]) {
        await saveDiscussionPoints(currentKPA.id, kpaDiscussionPoints[currentKPA.id])
      }

      await reviewService.updateReviewSession(sessionId, {
        is_complete: 1,
        // Freeze the KPA tree shape so an informal check-in can be re-opened
        // months later showing the objectives the conversation was actually
        // about, even if those objectives have since been retired or renamed.
        kpa_data_snapshot: activeKpaData,
        ...(ratingJustification.trim() ? { overall_comments: ratingJustification } : {})
      })

      toast.success('Check-in completed successfully!')

      if (onReviewCompleted) {
        onReviewCompleted()
      }

      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (error: any) {
      console.error('Failed to complete check-in:', error)
      toast.error(error?.response?.data?.detail || 'Failed to complete check-in. Please try again.')
    }
  }, [ensureReviewSession, kpaDiscussionPoints, saveDiscussionPoints, flushPendingSaves, ratingJustification, onClose, onReviewCompleted])

  // Tombstoned (soft-deleted) points are KEPT in the internal state so every
  // save re-transmits them and the backend's delete-wins merge stays converged
  // — but they must never surface in the UI or AI. Expose a filtered view as
  // the public `kpaDiscussionPoints`; internal save paths read the raw state
  // var directly, so the tombstones are still sent.
  const visibleKpaDiscussionPoints = useMemo(() => {
    const out: Record<string, { wentWell: DiscussionPoint[], challenges: DiscussionPoint[], actions: DiscussionPoint[] }> = {}
    for (const [kpaId, steps] of Object.entries(kpaDiscussionPoints)) {
      out[kpaId] = {
        wentWell: (steps.wentWell || []).filter(p => !p.deleted),
        challenges: (steps.challenges || []).filter(p => !p.deleted),
        actions: (steps.actions || []).filter(p => !p.deleted),
      }
    }
    return out
  }, [kpaDiscussionPoints])

  return {
    reviewSessionId,
    isReviewCompleted,
    tooSoonToRate,
    recordedByHr,
    calibrationRecordedByHr,
    setIsReviewCompleted,
    isSaving,
    isFormalReview,
    canRate,
    displayRatings,
    kpaRatings,
    setKpaRatings,
    overallRating,
    setOverallRating,
    ratingJustification,
    setRatingJustification,
    // Public view excludes soft-delete tombstones (raw state retained
    // internally for the save path so the backend keeps the deletion).
    kpaDiscussionPoints: visibleKpaDiscussionPoints,
    setKpaDiscussionPoints,
    kpaDataWithSnapshots,
    setKpaDataWithSnapshots,
    activeKpaData,
    transformKpaDataForPeriod,
    ensureReviewSession,
    saveKPARating,
    handleKPARating,
    saveDiscussionPoints,
    handleAddPoint,
    handleDeletePoint,
    handleUpdatePoint,
    handleCompleteReview,
    handleCompleteInformalReview,
    confirmCompleteOpen,
    setConfirmCompleteOpen,
  }
}
